import styled, { css } from 'styled-components'
import type { ResizeHandleAxis } from 'react-resizable'
import zLayers from 'utils/styling/zLayers'

type ResizeControlsProps = {
  rotate?: number
  disabled?: boolean
  selected?: boolean
}

/**
 * The frame element the controlled `Resizable` clones its handles into. Its
 * size is the inline width and height `ResizeControls.tsx` sets from the
 * item's live size, so the frame on screen is always the frame the page
 * holds — a clamp the page applies to a held handle reaches it even when the
 * pointer keeps asking for less.
 */
export const ResizeControls = styled.div<ResizeControlsProps>(
  ({ rotate, disabled }) => css`
    width: 100%;
    height: 100%;
    position: relative;
    outline: none;
    ${disabled && `z-index: ${zLayers.behindBackground};`}
    ${rotate ? `transform: rotate(${rotate}deg);` : ''}
    /* Prevent passive event listener warnings for resizable items */
    touch-action: ${disabled ? 'auto' : 'none'};
  `,
)

const ResizeHandlePosition: Partial<
  Record<ResizeHandleAxis, ReturnType<typeof css>>
> = {
  se: css`
    bottom: 2px;
    right: 2px;
  `,
  sw: css`
    bottom: 2px;
    left: 2px;
  `,
  ne: css`
    top: 2px;
    right: 2px;
  `,
  nw: css`
    top: 2px;
    left: 2px;
  `,
  e: css`
    top: 50%;
    right: 2px;
    transform: translateY(-50%);
  `,
  w: css`
    top: 50%;
    left: 2px;
    transform: translateY(-50%);
  `,
  s: css`
    bottom: 2px;
    left: 50%;
    transform: translateX(-50%);
  `,
  n: css`
    top: 2px;
    left: 50%;
    transform: translateX(-50%);
  `,
}

type ResizeHandleProps = {
  position?: ResizeHandleAxis
  selected?: boolean
  $itemLocked?: boolean
}

/**
 * The overlay's corner hit areas and pills own the pointer; this stays the
 * DraggableCore anchor — the overlay forwards its mousedown here via
 * dispatchEvent, which ignores pointer-events.
 */
export const ResizeHandle = styled.div<ResizeHandleProps>(
  ({ position, selected, $itemLocked }) => css`
    position: absolute;
    ${(position && ResizeHandlePosition[position]) || ResizeHandlePosition.se}
    width: 24px;
    height: 24px;
    border-bottom: solid 10px transparent;
    border-right: solid 10px transparent;
    border-left: solid 10px transparent;
    border-top: solid 10px transparent;
    cursor: ${$itemLocked ? 'default' : `${position}-resize`};
    z-index: ${selected ? zLayers.itemControlOverlay + 1 : 0};
    visibility: ${selected && !$itemLocked ? 'visible' : 'hidden'};
    pointer-events: none;
  `,
)
